import { useCallback, useMemo } from 'react';

import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import {
  EntityTypeahead,
  type SearchSelection,
} from '@/lib/crm/components/EntityTypeahead';
import type { SearchTypeName } from '@/lib/search';

export interface MergeRecordPickerDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: number;
  searchTypes: SearchTypeName[];
  currentEntityId: string;
  onPick: (entityId: string) => void;
}

// EntityTypeahead requires onChange, but this picker only cares about the
// terminal onSelect — selecting an entity closes the dialog immediately, so
// there is no controlled value to track.
function noopEntityTypeaheadChange(): void {}

export function MergeRecordPickerDialog({
  isOpen,
  onOpenChange,
  orgId,
  searchTypes,
  currentEntityId,
  onPick,
}: MergeRecordPickerDialogProps) {
  // Same-type search returns the record the picker was opened on; a record
  // can't be merged into itself, so never offer it as a candidate.
  const excludeEntityIds = useMemo(() => [currentEntityId], [currentEntityId]);

  const handleSelect = useCallback(
    (entity: SearchSelection) => {
      // Defensive: excludeEntityIds already hides the current record.
      if (entity.entity_id === currentEntityId) {
        return;
      }
      onPick(entity.entity_id);
      onOpenChange(false);
    },
    [currentEntityId, onPick, onOpenChange]
  );

  return (
    <ModalOverlay isOpen={isOpen} onOpenChange={onOpenChange}>
      <Modal className='max-w-lg'>
        <Dialog title='Merge records' instance='merge-records'>
          <div className='space-y-3 pb-2'>
            <p className='text-sm text-fg-secondary'>
              Search for the record to merge into this one.
            </p>
            <EntityTypeahead
              orgId={orgId}
              types={searchTypes}
              onChange={noopEntityTypeaheadChange}
              onSelect={handleSelect}
              excludeEntityIds={excludeEntityIds}
              placeholder='Search records'
            />
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
